import {API_URLS, VIEWS} from "../../URLS";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faAngleLeft, faPlus} from "@fortawesome/free-solid-svg-icons";
import {useNavigate} from "react-router-dom";
import {Card, Col, Modal, Row} from "react-bootstrap";
import {symptomTypeSort} from "../../enums";
import SymptomTypeQuestions from "./SymptomTypeQuestions";
import SymptomTypeItems from "./SymptomTypeItems";
import AddQuestionToSymptomType from "./AddQuestionToSymptomType";
import AddItemToSymptomType from "./AddItemToSymptomType";
import {
    DropDown,
    H2,
    Input,
    PrimaryButton,
    SecondaryButton,
    TextAreaWithLabelAndError,
    usePost
} from "@collabodoc/component-library";
import {useState} from "react";
import {useOidcAccessToken} from "@axa-fr/react-oidc";
import styled from "styled-components";

const AddSymptomTypeView = () => {
    const { accessToken } = useOidcAccessToken();
    const navigate = useNavigate();

    const { doPost } = usePost(
        API_URLS.ADDSYMPTOMTYPE,
        accessToken
    );

    const [formState, setFormState] = useState({
        multiCulturePhrase: {},
        sort: 0,
        svgIcon: "",
        questions: [],
        items: [],
        statusGroups: []
    });
    const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
    const [showAddItemModal, setShowAddItemModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const sortOptions = Object.entries(symptomTypeSort).map((x) => ({
        id:x[0],
        text: x[1]
    }));

    const sendData = async (e) => {
        e.preventDefault()
        setIsSubmitting(true);
        try{
            await doPost({
                sort: formState.sort,
                icon: formState.svgIcon,
                symptomTypeNames: formState.multiCulturePhrase,
                mappedQuestions: formState.questions.map(q => ({
                    patientQuestionId: q.patientQuestionTemplateId,
                    pageNumber: q.groupIndex,
                    patientViewOrder: q.index,
                    careGiverViewOrder: q.column,
                    journalSentenceIndex: q.journalSentenceIndex,
                })),
                mappedItems: formState.items.map(i => ({
                    itemTemplateId: i.itemTemplateId,
                    pageNumber: i.groupIndex,
                    patientViewOrder: i.index,
                    careGiverViewOrder: i.column,
                })),
            });

            setShowSuccessModal(true);
        }
        catch(error){
            setErrorMessage(error.message || "Ett fel uppstod");
            setShowErrorModal(true);
        }
        finally{
            setIsSubmitting(false);
        }
    };
    
    const handleFormChange = (key, value) => {

        if (key === "multiCulturePhrase") {
            const phrase = {
                multiCulturePhrase: {
                    ...formState[key],
                    ...value,
                },
            };
            setFormState((prevState) => ({
                ...prevState,
                ...phrase,
            }));
        } else {
            setFormState((prevState) => ({
                ...prevState,
                [key]: value,
            }));
        }
    };

    const handleRemoveQuestion = (e, id) => {

        e.stopPropagation();
        setFormState((prevState) => ({
            ...prevState,
            questions: prevState.questions.filter(
                (questions) => questions.patientQuestionTemplateId !== id,
            ),
        }));
    };

    const handleQuestionListChange = (
        id,
        phrase,
        groupIndex,
        index,
        column,
        journalSentenceIndex,
    ) => {

        setFormState((prevState) => ({
            ...prevState,
            questions: prevState.questions.map((q) =>
                q.patientQuestionTemplateId === id
                    ? {
                        patientQuestionTemplateId: id,
                        phrase,
                        groupIndex,
                        index,
                        column,
                        journalSentenceIndex
                    }
                    : q
            ),
        }));
    };

    const handleItemListChange = (
        id,
        phrase,
        groupTitle,
        itemType,
        groupIndex,
        index,
        column,
    ) => {
        setFormState((prevState) => ({
            ...prevState,
            items: prevState.items.map((i) =>
                i.itemTemplateId === id 
                    ? {
                        itemTemplateId: id,
                        phrase,
                        groupTitle,
                        itemType,
                        groupIndex,
                        index,
                        column
                       } 
                    : i
            ),
        }));
    }

    const handleAddQuestion = (id, phrase) => {
        setFormState((prevState) => ({
                ...prevState,
                questions: [
                    ...prevState.questions,
                    {
                        patientQuestionTemplateId: id,
                        phrase: phrase,
                        groupIndex: 0,
                        index: 0,
                        column: 0,
                        journalSentenceIndex: 0,
                    },
                ],
        }));
        setShowAddQuestionModal(false);
    };

    const handleAddItem = (id, phrase, groupTitle, itemType) => {
        setFormState((prevState) => ({
                ...prevState,
                items: [
                    ...prevState.items,
                    {
                        itemTemplateId: id,
                        phrase,
                        groupTitle,
                        itemType,
                        groupIndex: 0,
                        index: 0,
                        column: 0,
                    },
                ],
        }));

        setShowAddItemModal(false);
    };
    
    const handleRemoveItem = (e, id) => {
        e.stopPropagation();
        setFormState((prevState) => ({
            ...prevState,
            items: prevState.items.filter((items) => items.itemTemplateId !== id),
        }));
    };
    
    return (
        <>
            <form onSubmit={sendData}>
            <PageHeader>
                <PrimaryButton type="button" onClick={() => navigate(VIEWS.SYMPTOMTYPES)}>
                    <FontAwesomeIcon icon={faAngleLeft} />
                    <span>Till symptomtyper</span>
                </PrimaryButton>
                <div>
                    <MarginButton
                        type="button"
                        onClick={() => setShowAddQuestionModal(true)}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        <span>Lägg till fråga</span>
                    </MarginButton>
                    <MarginButton type="button" onClick={() => setShowAddItemModal(true)}>
                        <FontAwesomeIcon icon={faPlus} />
                        <span>Lägg till item</span>
                    </MarginButton>
                    <SaveButton
                        type="submit"
                        disabled ={isSubmitting}
                    >
                        <span>Spara ny symptomtyp</span>
                    </SaveButton>
                </div>
            </PageHeader>
            <Row>
                <Col lg={{ span: 4 }}>
                    <Card>
                        <Card.Header>
                            <H2>Skapa symptomtyp</H2>
                        </Card.Header>
                        <Card.Body>
                            <label htmlFor={"sv-SE"}>Namn (sv) (Obligatoriskt)</label>
                            <Input
                                id={"sv-SE"}
                                type={"text"}
                                required
                                value={formState.multiCulturePhrase["sv-SE"] ||""}
                                showError={!formState.multiCulturePhrase?.["sv-SE"]}
                                onChange={(e) =>
                                    handleFormChange("multiCulturePhrase", {
                                        "sv-SE": e.target.value,
                                    })
                                }
                            />
                            <label htmlFor={"en-US"}>Namn (en)</label>
                            <Input
                                id={"en-US"}
                                type={"text"}
                                value={formState.multiCulturePhrase["en-US"] || ""}
                                onChange={(e) =>
                                    handleFormChange("multiCulturePhrase", {
                                        "en-US": e.target.value,
                                    })
                                }
                                    
                            />
                            <label htmlFor={"de-DE"}>Namn (de)</label>
                            <Input
                                id={"de-DE"}
                                type={"text"}
                                value={formState.multiCulturePhrase["de-DE"] || ""}
                                onChange={(e) =>
                                    handleFormChange("multiCulturePhrase", {
                                        "de-DE": e.target.value,
                                    })
                                }
                            />
                            <TextAreaWithLabelAndError
                                value={formState.svgIcon || ""}
                                required
                                handleChange={(value) => handleFormChange("svgIcon", value)}
                                minRows={3}
                                maxRows={8}
                                label={"SVG Icon (Obligatoriskt)"}
                            />
                            <b>
                                <label>Sort</label>
                            </b>
                            <DropDown
                                options={sortOptions}
                                selected={symptomTypeSort[formState.sort]}
                                handler={(e) => handleFormChange("sort", Number(e))}
                            ></DropDown>
                        </Card.Body>
                    </Card>
                </Col>
                <SymptomTypeQuestions
                    symptomtypeQuestions={formState.questions}
                    handleRemoveQuestion={handleRemoveQuestion}
                    handleQuestionListChange={handleQuestionListChange}
                />
                <SymptomTypeItems
                    symptomtypeItems={formState.items}
                    handleRemoveItem={handleRemoveItem}
                    handleItemListChange={handleItemListChange}
                />
                <Modal
                    show={showAddQuestionModal}
                    onHide={() => setShowAddQuestionModal(false)}
                    dialogClassName={"modal-dialog modal-lg"}
                >
                    <AddQuestionToSymptomType
                        handleAddQuestion={handleAddQuestion}
                        symptomtypeQuestions={formState.questions}
                    />
                </Modal>
                <Modal
                    show={showAddItemModal}
                    onHide={() => setShowAddItemModal(false)}
                    dialogClassName={"modal-dialog modal-lg"}
                >
                    <AddItemToSymptomType
                        handleAddItem={handleAddItem}
                        symptomtypeItems={formState.items}
                    />
                </Modal>
                <Modal 
                    show={showSuccessModal}
                    onHide={() => {}}
                    dialogClassName={"modal-dialog modal-lg"}
                >
                    <Modal.Header>
                        <h2>Symptomtyp skapades</h2>
                    </Modal.Header>
                    <Modal.Body>
                        <PrimaryButton onClick={() => navigate(VIEWS.SYMPTOMTYPES)}>
                            OK
                        </PrimaryButton>
                    </Modal.Body>
                    
                </Modal>
                <Modal
                    show={showErrorModal}
                    onHide={() => setShowErrorModal(false)}
                    dialogClassName={"modal-dialog modal-lg"}
                >
                    <Modal.Header>
                        <h2>Ett fel uppstod</h2>
                    </Modal.Header>
                    <Modal.Body>
                        <p>{errorMessage}</p>
                        <PrimaryButton onClick={() => setShowErrorModal(false)}>
                            Tillbaka till skapa symptomtyp
                        </PrimaryButton>
                    </Modal.Body>
                    
                </Modal>
            </Row>
            </form>
        </>
   
    );
};

export default AddSymptomTypeView;

const MarginButton = styled(PrimaryButton)`
  margin-left: 5px;

  svg:first-child {
    margin-right: 5px;
  }
`;

const SaveButton = styled(SecondaryButton)`
    margin-left: 5px;

    svg:first-child {
        margin-right: 5px;
    }
`;

const PageHeader = styled.section`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
`;